import { getUserRoleInOrganization } from 'api/user';
import { getAllUsersForAdmin } from 'api/users';
import BorderGradient from 'components/shared/BorderGradient/BorderGradient';
import { useOrganization } from 'hooks/OrganizationContext';
import React, { useState, useEffect } from 'react';
import { projectsapi } from 'utilities/Axios';
import Select from "react-select";

interface OrganizationModalProps {
  isOpen: boolean;
  onClose: () => void;
  mode: 'edit' | 'create';
  initialData?: { id: string; name: string; desc: string };
  setRefreshMode: (value: string) => void;
}

const OrganizationModal: React.FC<OrganizationModalProps> = ({
  isOpen,
  onClose,
  mode,
  initialData = { id: '', name: '', desc: '' },
  setRefreshMode,
}) => {
  const [formData, setFormData] = useState(initialData);
  const [isLoading, setIsLoading] = useState(false);
  const [users, setUsers] = useState([]);
  const [selected, setSelected] = React.useState(null);

  const [userRole, setUserRole] = useState<string[]>([]);
  const { selectedOrganization } = useOrganization();
  const getUserRole = async () => {
    const response: any = await getUserRoleInOrganization(selectedOrganization?.id);
    if (response.status === 200) {
      setUserRole(response.data?.data?.roles || []);
    }
  };



  useEffect(() => {
    getUserRole();
  }, []);

  useEffect(() => {
    if (userRole.includes('SUPERADMIN')) {
      const fetchUsers = async () => {
        const response: any = await getAllUsersForAdmin();
        if (response.status === 200) {
          setUsers(response.data.map((user: any) => ({
            value: user.id,
            label: user.name
          })));
        }
      };
      fetchUsers();
    }
  }, [userRole]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsLoading(true);
    try {
      if (mode === 'create') {
        await projectsapi.post('/api/Organisation', formData);
      } else {
        await projectsapi.put(`/api/Organisation/${formData.id}`, formData);
      }
      setRefreshMode('organizations');
      onClose();
    } catch (error) {
      console.error('Error:', error);
    }
    setIsLoading(false);
  };

  useEffect(() => {
    if (initialData?.id !== '') {
      setFormData(initialData);
    }
  }, [initialData]);

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 flex items-center justify-center z-50 ">
      <BorderGradient className="w-full max-w-2xl p-6">

        <div className="bg-dark rounded-xl border border-white/[0.08] w-full max-w-2xl p-6">
          <div className="flex justify-between items-center mb-6">
            <h2 className="text-xl text-white font-medium">
              {mode === 'create' ? 'Create Organization' : 'Edit Organization'}
            </h2>
            <button
              onClick={onClose}
              className="text-gray-400 hover:text-white"
            >
              ✕
            </button>
          </div>

          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <label className="block text-sm text-gray-400 mb-1">Organization Name</label>
              <input
                type="text"
                value={formData?.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                className="w-full bg-[#2A2A2A] border border-white/[0.08] rounded-lg px-4 py-2 text-white focus:outline-none focus:border-blue-500"
                placeholder="Enter organization name"
                required
              />
            </div>


            {
              userRole.includes('SUPERADMIN') &&
              <div className="w-full">
              <label
                htmlFor="ownerId"
                className="block text-sm font-medium text-gray-300 mb-1"
              >
                Organization Owner
              </label>
              <Select
                id="ownerId"
                name="ownerId"
                options={users}
                value={selected}
                onChange={setSelected} 
                className="react-select-container"
                classNamePrefix="react-select"
                styles={{
                  control: (base) => ({
                    ...base,
                    backgroundColor: "#2A2A2A",
                    borderColor: "rgba(255,255,255,0.08)",
                    borderRadius: "0.5rem",
                    padding: "4px 8px",
                    color: "white",
                  }),
                  singleValue: (base) => ({
                    ...base,
                    color: "white",
                  }),
                  input: (base) => ({
                    ...base,
                    color: "white",
                  }),
                  placeholder: (base) => ({
                    ...base,
                    color: "rgba(255,255,255,0.6)",
                  }),
                  menu: (base) => ({
                    ...base,
                    backgroundColor: "#2A2A2A",
                    maxHeight: 160,
                  }),
                  menuList: (base) => ({
                    ...base,
                    maxHeight: 160,
                    overflowY: "auto",
                  }),
                  option: (base, state) => ({
                    ...base,
                    backgroundColor: state.isFocused ? "rgba(59,130,246,0.5)" : "#2A2A2A",
                    color: "white",
                    cursor: "pointer",
                  }),
                }}
              />
            </div>
            }

            <div>
              <label className="block text-sm text-gray-400 mb-1">Description</label>
              <textarea
                // type="text"
                value={formData?.desc}
                onChange={(e) => setFormData({ ...formData, desc: e.target.value })}
                className="w-full bg-[#2A2A2A] border border-white/[0.08] rounded-lg px-4 py-2 text-white focus:outline-none focus:border-blue-500"
                placeholder="Enter description"
                rows={4}
                required
              />
            </div>

            <div className="flex justify-end gap-3 mt-6">
              <button
                type="button"
                onClick={onClose}
                className="px-4 py-2 text-gray-400 hover:text-white"
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={isLoading}
                className="px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg disabled:opacity-50"
              >
                {isLoading ? 'Processing...' : mode === 'create' ? 'Create' : 'Update'}
              </button>
            </div>
          </form>
        </div>

      </BorderGradient>
    </div>
  );
};

export default OrganizationModal; 